import React from 'react';
import { Button, Container, Label } from '@playcanvas/pcui/react';
import { PackageManifestEx, ManifestData } from '../types';

interface ManifestDialogProps {
    manifestData: ManifestData;
    onClose: () => void;
}

const ManifestDialog: React.FC<ManifestDialogProps> = ({ manifestData, onClose }) => {
    if (!manifestData.loaded || !manifestData.manifest) {
        return null;
    }

    const manifest = manifestData.manifest;
    
    const modelCount = manifest.modelPackages?.length || 0;
    const liveryCount = manifest.liveryPackages?.length || 0;
    const configCount = manifest.configurationPackages?.length || 0;
    const soundCount = manifest.soundPackages?.length || 0;
    const sharedCount = manifest.sharedPackages?.length || 0;
    const totalCount = modelCount + liveryCount + configCount + soundCount + sharedCount;

    // Calculate total size
    const totalSize = [
        ...(manifest.modelPackages || []),
        ...(manifest.liveryPackages || []),
        ...(manifest.configurationPackages || []),
        ...(manifest.soundPackages || []),
        ...(manifest.sharedPackages || [])
    ].reduce((sum, pkg) => sum + (pkg.size || 0), 0);

    const formatSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    const importDate = manifestData.importedAt 
        ? new Date(manifestData.importedAt).toLocaleString()
        : 'Unknown';

    return (
        <div id="manifest-dialog-overlay" onClick={onClose}>
            <div id="manifest-dialog" onClick={(e) => e.stopPropagation()}>
                <Container class={['manifest-dialog-content']} flex>
                    <div className="manifest-dialog-header">
                        <Label text="Package Manifest Imported" class={['manifest-dialog-title']} />
                        <Button icon="E132" onClick={onClose} class={['close-button']} />
                    </div>
                    
                    <div className="manifest-dialog-body">
                        <div className="manifest-stat-row">
                            <Label text="Import Date:" class={['manifest-label']} />
                            <Label text={importDate} class={['manifest-value']} />
                        </div>
                        
                        <div className="manifest-stat-row">
                            <Label text="Total Packages:" class={['manifest-label']} />
                            <Label text={totalCount.toString()} class={['manifest-value', 'highlight']} />
                        </div>
                        
                        <div className="manifest-stat-section">
                            <Label text="Package Breakdown:" class={['manifest-section-title']} />
                            
                            {modelCount > 0 && (
                                <div className="manifest-stat-row indent">
                                    <Label text="Models:" class={['manifest-label']} />
                                    <Label text={modelCount.toString()} class={['manifest-value']} />
                                </div>
                            )}
                            
                            {liveryCount > 0 && (
                                <div className="manifest-stat-row indent">
                                    <Label text="Liveries:" class={['manifest-label']} />
                                    <Label text={liveryCount.toString()} class={['manifest-value']} />
                                </div>
                            )}
                            
                            {configCount > 0 && (
                                <div className="manifest-stat-row indent">
                                    <Label text="Configurations:" class={['manifest-label']} />
                                    <Label text={configCount.toString()} class={['manifest-value']} />
                                </div>
                            )}
                            
                            {soundCount > 0 && (
                                <div className="manifest-stat-row indent">
                                    <Label text="Sounds:" class={['manifest-label']} />
                                    <Label text={soundCount.toString()} class={['manifest-value']} />
                                </div>
                            )}
                            
                            {sharedCount > 0 && (
                                <div className="manifest-stat-row indent">
                                    <Label text="Shared:" class={['manifest-label']} />
                                    <Label text={sharedCount.toString()} class={['manifest-value']} />
                                </div>
                            )}
                        </div>
                        
                        <div className="manifest-stat-row">
                            <Label text="Total Size:" class={['manifest-label']} />
                            <Label text={formatSize(totalSize)} class={['manifest-value', 'highlight']} />
                        </div>
                    </div>
                    
                    <div className="manifest-dialog-footer">
                        <Button text="OK" onClick={onClose} class={['primary']} />
                    </div>
                </Container>
            </div>
        </div>
    );
};

export default ManifestDialog;

